<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { Trash2, RefreshCw } from 'lucide-vue-next';
import { computed, inject, ref } from 'vue';
import type { Ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { ExtractionSnapshot } from '@/lib/editor/extraction-table-extension';
import { snapshot as extractionSnapshot } from '@/routes/projects/extraction';

// ── Props ─────────────────────────────────────────────────────────────

const props = defineProps<NodeViewProps>();

// ── Project ID (for snapshot refresh) ────────────────────────────────
const projectId = inject<string>('editorProjectId', '');

// ── Counter (shared with numbered-table-extension, same "Tableau N") ──

const tableCounters = inject<Readonly<Ref<ReadonlyMap<string, number>>>>(
    'editorTableCounters',
);
const tableNumber = computed(() => {
    const id: string = props.node.attrs.tableId;

    return id && tableCounters?.value
        ? (tableCounters.value.get(id) ?? '?')
        : '?';
});

// ── Snapshot ──────────────────────────────────────────────────────────

const snapshot = computed<ExtractionSnapshot | null>(
    () => props.node.attrs.snapshotData ?? null,
);

// ── Caption ───────────────────────────────────────────────────────────

const caption = ref<string>(props.node.attrs.caption ?? '');
function onCaptionBlur() {
    props.updateAttributes({ caption: caption.value });
}

// ── Status badge colours ──────────────────────────────────────────────

function statusClass(status: string | null): string {
    switch (status) {
        case 'validated':
            return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
        case 'extracted':
            return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300';
        case 'rejected':
            return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
        default:
            return 'bg-muted text-muted-foreground';
    }
}

// ── Refresh snapshot ──────────────────────────────────────────────────

const isRefreshing = ref(false);
const showRefreshConfirm = ref(false);

async function refreshSnapshot() {
    showRefreshConfirm.value = false;
    isRefreshing.value = true;

    const columnIds: string[] = props.node.attrs.columnIds ?? [];
    const filter: string = props.node.attrs.filter ?? 'all';

    if (!projectId) {
        isRefreshing.value = false;

        return;
    }

    try {
        const params = new URLSearchParams({ status: filter });

        for (const id of columnIds) {
            params.append('columns[]', id);
        }

        const res = await fetch(
            `${extractionSnapshot.url(projectId)}?${params.toString()}`,
            {
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            },
        );

        if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
        }

        const data: ExtractionSnapshot = await res.json();
        props.updateAttributes({ snapshotData: data });
    } catch {
        // silently ignore — the old snapshot remains intact
    } finally {
        isRefreshing.value = false;
    }
}
</script>

<template>
    <NodeViewWrapper
        class="extraction-table-wrapper node-insert-animate not-prose my-6"
        data-drag-handle
    >
        <!-- Header -->
        <div class="mb-1 flex items-center gap-2">
            <span class="shrink-0 text-sm font-semibold"
                >Tableau {{ tableNumber }} :</span
            >
            <Input
                v-if="props.editor?.isEditable"
                v-model="caption"
                class="h-6 border-0 border-b border-dashed bg-transparent px-1 text-sm shadow-none focus-visible:ring-0"
                placeholder="Titre du tableau…"
                @blur="onCaptionBlur"
            />
            <span v-else class="text-sm text-muted-foreground">{{
                caption
            }}</span>

            <template v-if="props.editor?.isEditable">
                <!-- Refresh -->
                <div class="relative ml-auto shrink-0">
                    <Button
                        v-if="!showRefreshConfirm"
                        variant="ghost"
                        size="icon"
                        class="h-6 w-6"
                        title="Rafraîchir le snapshot"
                        :disabled="isRefreshing"
                        @click="showRefreshConfirm = true"
                    >
                        <RefreshCw
                            class="h-3.5 w-3.5"
                            :class="{ 'animate-spin': isRefreshing }"
                        />
                    </Button>

                    <!-- Inline confirm -->
                    <div
                        v-if="showRefreshConfirm"
                        class="flex items-center gap-1 rounded border bg-background px-2 py-1 text-xs shadow-md"
                    >
                        <span>Mettre à jour le snapshot ?</span>
                        <Button
                            size="sm"
                            class="h-5 px-2 text-xs"
                            @click="refreshSnapshot"
                            >Oui</Button
                        >
                        <Button
                            variant="ghost"
                            size="sm"
                            class="h-5 px-2 text-xs"
                            @click="showRefreshConfirm = false"
                            >Non</Button
                        >
                    </div>
                </div>

                <!-- Delete -->
                <Button
                    variant="ghost"
                    size="icon"
                    class="h-6 w-6 shrink-0 text-destructive hover:text-destructive"
                    title="Supprimer le tableau"
                    @click="props.deleteNode()"
                >
                    <Trash2 class="h-3.5 w-3.5" />
                </Button>
            </template>
        </div>

        <!-- Empty state -->
        <div
            v-if="
                !snapshot || !snapshot.columns || snapshot.columns.length === 0
            "
            class="rounded border border-dashed p-6 text-center text-sm text-muted-foreground"
        >
            Aucune donnée dans ce snapshot.
        </div>

        <!-- Table -->
        <div v-else class="overflow-x-auto rounded border">
            <table class="w-full border-collapse text-sm">
                <thead>
                    <tr class="bg-muted/60">
                        <th
                            class="border-b px-3 py-2 text-left font-medium text-muted-foreground"
                        >
                            Article
                        </th>
                        <th
                            v-for="col in snapshot.columns"
                            :key="col.id"
                            class="border-b border-l px-3 py-2 text-left font-medium text-muted-foreground"
                        >
                            {{ col.name }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(row, idx) in snapshot.rows"
                        :key="row.paperId"
                        :class="idx % 2 === 1 ? 'bg-muted/20' : ''"
                    >
                        <td
                            class="max-w-[14rem] truncate px-3 py-1.5 align-top font-medium"
                        >
                            {{ row.paperLabel }}
                        </td>
                        <td
                            v-for="col in snapshot.columns"
                            :key="col.id"
                            class="border-l px-3 py-1.5 align-top"
                        >
                            <template
                                v-if="
                                    row.cells[col.id]?.value !== null &&
                                    row.cells[col.id]?.value !== undefined
                                "
                            >
                                <span class="block">{{
                                    row.cells[col.id]!.value
                                }}</span>
                                <span
                                    v-if="row.cells[col.id]?.status"
                                    :class="[
                                        'mt-0.5 inline-block rounded px-1 py-0.5 text-[10px] font-medium',
                                        statusClass(row.cells[col.id]!.status),
                                    ]"
                                >
                                    {{ row.cells[col.id]!.status }}
                                </span>
                            </template>
                            <span v-else class="text-muted-foreground/40"
                                >—</span
                            >
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Snapshot timestamp -->
        <p
            v-if="snapshot?.snapshotAt"
            class="mt-1 text-right text-[10px] text-muted-foreground/50"
        >
            Snapshot : {{ new Date(snapshot.snapshotAt).toLocaleString() }}
        </p>
    </NodeViewWrapper>
</template>
